import React, { useState, useEffect, useRef } from 'react';
import { formatCurrency } from '../../lib/format';
import { X, Download, Search, Calendar, Package, DollarSign, ChevronLeft, ChevronRight } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { useEscapeKey } from '../../hooks/useKeyboardShortcuts';
import { useUser } from '../../store/useAuthStore';

interface Order {
  orderId: string;
  orderDate: Date;
  physicianName: string;
  accountManagerName?: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalAmount: number;
  orderType: 'single' | 'bulk';
  paymentMethod: 'physician' | 'patient';
  revenueAmount: number;
  revenueSource: string;
  // Cost breakdown — role-dependent. Backend strips upstream tiers per role:
  //   admin:       pharmacyCost, adminMargin, supervisorMargin, distributorMargin, physicianMargin
  //   supervisor:  productCost, supervisorMargin, distributorMargin, physicianMargin
  //   distributor: productCost, distributorMargin, physicianMargin
  //   physician:   clinicCost, physicianMargin
  pharmacyCost?: number;
  adminMargin?: number;
  productCost?: number;
  clinicCost?: number;
  supervisorMargin?: number;
  distributorMargin?: number;
  physicianMargin: number;
}

interface PaginationInfo {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
  hasMore: boolean;
}

interface ColumnTotals {
  pharmacyCost?: number;
  adminMargin?: number;
  supervisorMargin?: number;
  productCost?: number;
  clinicCost?: number;
  distributorMargin?: number;
  physicianMargin: number;
  totalAmount: number;
}

interface RevenueBreakdownData {
  revenueType: string;
  totalAmount: number;
  orderCount: number;
  orders: Order[];
  pagination: PaginationInfo;
  columnTotals?: ColumnTotals;
}

interface RevenueBreakdownModalProps {
  isOpen: boolean;
  onClose: () => void;
  revenueType: string;
  title: string;
  dateRange?: { startDate?: Date; endDate?: Date };
}


const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(date));
};

const formatDateRange = (dateRange?: { startDate?: Date; endDate?: Date }): string => {
  if (!dateRange?.startDate || !dateRange?.endDate) {
    return 'All Time';
  }
  const formatShort = (date: Date) => new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(new Date(date));
  return `${formatShort(dateRange.startDate)} - ${formatShort(dateRange.endDate)}`;
};

const getStandardMetricTitle = (title: string): string => {
  const normalized = title.trim();

  const titleMap: Record<string, string> = {
    'AM Margin': 'Account Manager Margin',
    'AM Margins': 'Account Manager Margins',
    'Your AM Margin': 'Your Account Manager Margin',
    'Account Manager Margin': 'Account Manager Margin',
    'Product Cost': 'Product Cost',
    'Pharmacy Cost': 'Pharmacy Cost',
    'Admin Margin': 'Admin Margin',
    'Supervisor Margin': 'Supervisor Margin',
    'Total Revenue': 'Total Revenue',
    'Total Network Revenue': 'Total Network Revenue',
    'Your Physician Revenue': 'Physician Revenue',
    'System Revenue': 'Total Revenue'
  };

  return titleMap[normalized] ?? normalized;
};

export const RevenueBreakdownModal: React.FC<RevenueBreakdownModalProps> = ({
  isOpen,
  onClose,
  revenueType,
  title,
  dateRange
}) => {
  const user = useUser();
  const isAdmin = user?.role === 'admin';
  const isSupervisor = user?.role === 'supervisor';
  const isPhysician = user?.role === 'physician' || user?.role === 'physician_readonly';
  const standardizedTitle = getStandardMetricTitle(title);

  // Physicians don't get a revenue drill-down — the dashboard cards are not
  // clickable for them and the modal should never render even if mounted.
  if (isPhysician) {
    return null;
  }

  const [data, setData] = useState<RevenueBreakdownData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const fetchSeq = useRef(0);
  const [sortField, setSortField] = useState<keyof Order>('orderDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);

  // ESC key to close modal
  useEscapeKey(isOpen, onClose);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Search and sort run on the server over every row (not just this page), so
  // any change to them, like opening the modal, starts again from page 1.
  useEffect(() => {
    if (isOpen && revenueType) {
      setCurrentPage(1);
      fetchRevenueBreakdown(1);
    }
  }, [isOpen, revenueType, dateRange, debouncedSearch, sortField, sortDirection]);

  const fetchRevenueBreakdown = async (page: number = currentPage) => {
    const mine = ++fetchSeq.current;
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.getRevenueBreakdown(revenueType, dateRange, page, pageSize, {
        search: debouncedSearch || undefined,
        sortBy: sortField,
        sortOrder: sortDirection
      });
      // A slower answer to an older search/sort/page must not overwrite a newer one.
      if (mine !== fetchSeq.current) return;

      if (response.success) {
        setData(response.data);
        setCurrentPage(page);
      } else {
        setError(response.error || 'Failed to load revenue breakdown');
      }
    } catch (error: any) {
      if (mine !== fetchSeq.current) return;
      console.error('Revenue breakdown exception:', error);
      setError('Failed to load revenue breakdown');
    } finally {
      if (mine === fetchSeq.current) setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && (!data?.pagination || newPage <= data.pagination.totalPages)) {
      fetchRevenueBreakdown(newPage);
    }
  };

  const handleSort = (field: keyof Order) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Already searched, sorted and paged by the server.
  const filteredAndSortedOrders = data?.orders ?? [];

  const exportToCSV = () => {
    if (!data?.orders) return;

    const baseHeaders = [
      'Order ID', 'Date', 'Physician', 'Account Manager',
      'Product', 'Quantity', 'Unit Price', 'Payment Method'
    ];

    // Role-based cost columns
    let costHeaders: string[];
    if (isAdmin) {
      costHeaders = ['Pharmacy Cost', 'Admin Margin', 'Supervisor Margin', 'Account Manager Margin', 'Physician Margin'];
    } else if (isSupervisor) {
      costHeaders = ['Product Cost', 'Supervisor Margin', 'Account Manager Margin', 'Physician Margin'];
    } else {
      costHeaders = ['Product Cost', 'Account Manager Margin', 'Physician Margin'];
    }

    const headers = [...baseHeaders, ...costHeaders, 'Total Amount'];

    const csvContent = [
      headers.join(','),
      ...filteredAndSortedOrders.map(order => {
        const baseFields = [
          order.orderId,
          formatDate(order.orderDate),
          `"${order.physicianName}"`,
          `"${order.accountManagerName || 'Unassigned'}"`,
          `"${order.productName}"`,
          order.quantity,
          order.unitPrice,
          order.paymentMethod,
        ];

        let costFields: (number | undefined)[];
        if (isAdmin) {
          costFields = [order.pharmacyCost ?? 0, order.adminMargin ?? 0, order.supervisorMargin ?? 0, order.distributorMargin ?? 0, order.physicianMargin];
        } else if (isSupervisor) {
          costFields = [order.productCost ?? 0, order.supervisorMargin ?? 0, order.distributorMargin ?? 0, order.physicianMargin];
        } else {
          costFields = [order.productCost ?? 0, order.distributorMargin ?? 0, order.physicianMargin];
        }

        return [...baseFields, ...costFields, order.totalAmount].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title.replace(/\s+/g, '_')}_breakdown.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-7xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white">Realized {standardizedTitle} Breakdown</h2>
              <div className="flex items-center gap-3 mt-1">
                <p className="text-slate-200 text-sm">
                  This drill-down shows only realized amounts: orders and invoices that have already been paid.
                </p>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-white/15 rounded-full text-xs font-medium">
                  <Calendar className="w-3 h-3" />
                  {formatDateRange(dateRange)}
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={exportToCSV}
                disabled={loading || !data?.orders?.length}
                className="flex items-center space-x-2 px-4 py-2 bg-white/15 hover:bg-white/25 disabled:bg-white/5 disabled:opacity-50 rounded-lg text-sm font-medium transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>Export CSV</span>
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/15 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading && !data && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
              <span className="ml-3 text-gray-600">Loading revenue breakdown...</span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800">{error}</p>
              <button
                onClick={() => fetchRevenueBreakdown()}
                className="mt-2 text-red-600 hover:text-red-800 text-sm font-medium"
              >
                Try again
              </button>
            </div>
          )}

          {/* Stays mounted while a new search loads so the search box keeps focus. */}
          {data && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <DollarSign className="w-8 h-8 text-green-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-600">Realized {standardizedTitle}</p>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(data.totalAmount)}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <Package className="w-8 h-8 text-sky-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-600">Realized Orders</p>
                      <p className="text-2xl font-bold text-gray-900">{data.orderCount.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <Calendar className="w-8 h-8 text-purple-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-600">Realized Avg / Order</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {data.orderCount > 0 ? formatCurrency(data.totalAmount / data.orderCount) : '$0.00'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Search */}
              <div className="mb-6">
                <div className="mb-3 rounded-xl border border-sky-100 bg-sky-50 px-4 py-3 text-sm text-sky-900">
                  <p className="font-medium">What you are looking at</p>
                  <p className="mt-1 text-sky-800">
                    Every number in this table is realized only. Booked receivables and pipeline orders are not included in this drill-down.
                  </p>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search orders by physician, account manager, product, or order ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Orders Table */}
              <div className="overflow-x-auto overflow-y-auto max-h-96">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('orderDate')}
                      >
                        Date {sortField === 'orderDate' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('physicianName')}
                      >
                        Physician {sortField === 'physicianName' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('productName')}
                      >
                        Product {sortField === 'productName' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('quantity')}
                      >
                        Qty {sortField === 'quantity' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('paymentMethod')}
                      >
                        Payment {sortField === 'paymentMethod' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      {/* Role-based cost columns */}
                      {isAdmin ? (
                        <>
                          <th
                            className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleSort('pharmacyCost')}
                          >
                            Pharmacy Cost {sortField === 'pharmacyCost' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </th>
                          <th
                            className="px-4 py-3 text-right text-xs font-medium text-green-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleSort('adminMargin')}
                          >
                            Admin Margin {sortField === 'adminMargin' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </th>
                          <th
                            className="px-4 py-3 text-right text-xs font-medium text-orange-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleSort('supervisorMargin')}
                          >
                            Supervisor Margin {sortField === 'supervisorMargin' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </th>
                        </>
                      ) : (
                        <th
                          className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('productCost')}
                        >
                          Product Cost {sortField === 'productCost' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                      )}
                      {isSupervisor && (
                        <th
                          className="px-4 py-3 text-right text-xs font-medium text-orange-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('supervisorMargin')}
                        >
                          Supervisor Margin {sortField === 'supervisorMargin' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                      )}
                      <th
                        className="px-4 py-3 text-right text-xs font-medium text-red-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('distributorMargin')}
                      >
                        Account Manager Margin {sortField === 'distributorMargin' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th
                        className="px-4 py-3 text-right text-xs font-medium text-sky-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('physicianMargin')}
                      >
                        Physician Margin {sortField === 'physicianMargin' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th
                        className="px-4 py-3 text-right text-xs font-medium text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-gray-100 font-bold"
                        onClick={() => handleSort('totalAmount')}
                      >
                        Realized Total {sortField === 'totalAmount' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredAndSortedOrders.map((order, index) => (
                      <tr key={`${order.orderType}-${order.orderId}-${index}`} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">
                          {formatDate(order.orderDate)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{order.physicianName}</div>
                          {order.accountManagerName && order.accountManagerName !== 'Unassigned' && (
                            <div className="text-xs text-gray-500">{order.accountManagerName}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 max-w-[150px] truncate" title={order.productName}>
                          {order.productName}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-center">
                          {order.quantity}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            order.paymentMethod === 'patient'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {order.paymentMethod === 'patient' ? 'Patient' : 'Clinic'}
                          </span>
                        </td>
                        {/* Role-based cost cells */}
                        {isAdmin ? (
                          <>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-600">
                              {formatCurrency(order.pharmacyCost ?? 0)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-green-600">
                              {formatCurrency(order.adminMargin ?? 0)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-orange-600">
                              {formatCurrency(order.supervisorMargin ?? 0)}
                            </td>
                          </>
                        ) : (
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-600">
                            {formatCurrency(order.productCost ?? 0)}
                          </td>
                        )}
                        {isSupervisor && (
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-orange-600">
                            {formatCurrency(order.supervisorMargin ?? 0)}
                          </td>
                        )}
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-red-600">
                          {formatCurrency(order.distributorMargin ?? 0)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-sky-600">
                          {formatCurrency(order.physicianMargin)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold text-gray-900">
                          {formatCurrency(order.totalAmount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {data?.columnTotals && filteredAndSortedOrders.length > 0 && (
                    <tfoot className="bg-gray-50 border-t-2 border-gray-300 sticky bottom-0">
                      <tr>
                        <td className="px-4 py-3 text-xs font-bold text-gray-900 uppercase" colSpan={5}>
                          Totals (all pages)
                        </td>
                        {isAdmin ? (
                          <>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold text-gray-700">
                              {formatCurrency(data.columnTotals.pharmacyCost ?? 0)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold text-green-700">
                              {formatCurrency(data.columnTotals.adminMargin ?? 0)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold text-orange-700">
                              {formatCurrency(data.columnTotals.supervisorMargin ?? 0)}
                            </td>
                          </>
                        ) : (
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold text-gray-700">
                            {formatCurrency(data.columnTotals.productCost ?? 0)}
                          </td>
                        )}
                        {isSupervisor && (
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold text-orange-700">
                            {formatCurrency(data.columnTotals.supervisorMargin ?? 0)}
                          </td>
                        )}
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold text-red-700">
                          {formatCurrency(data.columnTotals.distributorMargin ?? 0)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold text-sky-700">
                          {formatCurrency(data.columnTotals.physicianMargin)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold text-gray-900">
                          {formatCurrency(data.columnTotals.totalAmount)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>

                {filteredAndSortedOrders.length === 0 && !loading && (
                  <div className="text-center py-12">
                    <Package className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No orders found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {searchTerm ? 'Try adjusting your search terms.' : 'No orders or invoices contributed to this revenue.'}
                    </p>
                  </div>
                )}
              </div>

              {/* Pagination Controls */}
              {data?.pagination && data.pagination.totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-4">
                  <div className="flex flex-1 justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1 || loading}
                      className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!data.pagination.hasMore || loading}
                      className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing{' '}
                        <span className="font-medium">
                          {((currentPage - 1) * data.pagination.limit) + 1}
                        </span>
                        {' '}to{' '}
                        <span className="font-medium">
                          {Math.min(currentPage * data.pagination.limit, data.pagination.totalCount)}
                        </span>
                        {' '}of{' '}
                        <span className="font-medium">{data.pagination.totalCount}</span>
                        {' '}results
                      </p>
                    </div>
                    <div>
                      <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1 || loading}
                          className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Previous</span>
                          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                        </button>
                        
                        {/* Page numbers */}
                        {Array.from({ length: Math.min(5, data.pagination.totalPages) }, (_, i) => {
                          let pageNum: number;
                          if (data.pagination.totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= data.pagination.totalPages - 2) {
                            pageNum = data.pagination.totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              disabled={loading}
                              className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                currentPage === pageNum
                                  ? 'z-10 bg-sky-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600'
                                  : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={!data.pagination.hasMore || loading}
                          className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Next</span>
                          <ChevronRight className="h-5 w-5" aria-hidden="true" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
